import classNames from 'classnames';
import React, { useEffect, useState, useMemo, useRef } from 'react';

import '../../../../../../scss/live-event/base/modules/agenda.scss';
import '../../../../../../scss/live-event/base/modules/session-filter.scss';
import './agenda-editor.scss';
import {
	AgendaContent,
	PageModule,
	Session,
	SessionDisplayAll,
	SessionDisplayOptions,
	SessionPreview,
	SessionTypesEnum,
} from '../../../../../../types/working-model';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import { UpdatePageModule } from '../../../../../../connection/page-modules';
import Search from '../../../../../general-ui/search-bar/search';
import ModalComponent from '../../../../../general-ui/modal/modal';
import { updateHomepageModule, updateCustomPageModule } from '../../../../../../store/actions/admin/create-event';

import { useAppDispatch, useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';

import { AgendaCarousel } from '../../../../../live-event/modules/agenda/agenda-carousel';
import {
	fillOutSessionDisplayOptions,
	getAllTrackNames,
	getDisplayedTracksInSpecifiedOrder,
	getOnDemandSessionsSelectedForDisplay,
	noTrackName,
	scheduledSessionTypes,
} from '../../../../../live-event/modules/agenda/agenda-utils';

import { isArray, isBoolean } from 'underscore';
import CarouselCard from './carousel-card';
import CarouselGroupDropdown from './carousel-group-dropdown';

export interface IManageAgendaProps {
	page_module: PageModule;
	modalIsOpen: boolean;
	setModalClosed: () => void;
	openReorderModal: () => void;
	isLandingPage?: boolean;
	isHomePage?: boolean;
	isCustomPage?: boolean;
	customPageId?: number;
}

const ManageAgenda: React.FC<IManageAgendaProps> = ((props: IManageAgendaProps) => {
	const {
		page_module: module,
		modalIsOpen,
		setModalClosed,
		openReorderModal,
		isLandingPage = false,
		isHomePage = false,
		isCustomPage = false,
		customPageId,
	} = props;

	if (!module.is_on) return null;

	if (isCustomPage && !customPageId) {
		console.error(`Specified custom page module type, but no pageId was specified.  Module is ${module}.`);
		return null;
	}

	const dispatch = useAppDispatch();

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const sessions = useMemo(() => workingEvent?.sessions || [], [workingEvent]);
	const sessionDisplayAll = useMemo(() => module.module_display_info?.session_display_all || { liveSessions: true, onDemandSessions: true }, [module.module_display_info?.session_display_all]);
	const sessionDisplayOptions = useMemo(() => module.module_display_info?.session_display_options || {}, [module.module_display_info?.session_display_options]);
	const ondemandTrackOrder = useMemo(() =>
		getDisplayedTracksInSpecifiedOrder(
			sessions,
			module?.module_display_info?.content_track_order || []
		), [sessions, module.module_display_info?.content_track_order]);

	const moduleContent = module.content as AgendaContent;

	const [trackedSessionDisplayOptions, setTrackedSessionDisplayOptions] = useState<SessionDisplayOptions>({});
	const [trackedSessionDisplayAll, setTrackedSessionDisplayAll] = useState<SessionDisplayAll>({ liveSessions: true, onDemandSessions: true });
	const [savingSessions, setSavingSessions] = useState(false);

	const [expandedBroadcast, setExpandedBroadcast] = useState(true);
	const [expandedOndemand, setExpandedOndemand] = useState(true);

	const [filteredSessions, setFilteredSessions] = useState<(Session | SessionPreview)[]>([]);
	const [searching, setSearching] = useState(false);

	const [broadcastTracks, setBroadcastTracks] = useState<({ [key: string]: (Session | SessionPreview)[] })>({});
	const [ondemandTracks, setOndemandTracks] = useState<({ [key: string]: (Session | SessionPreview)[] })>({});
	// Same as above, but not subject to filtering by search
	const [allBroadcastTracks, setAllBroadcastTracks] = useState<({ [key: string]: (Session | SessionPreview)[] })>({});
	const [allOndemandTracks, setAllOndemandTracks] = useState<({ [key: string]: (Session | SessionPreview)[] })>({});

	const [blockClose, setBlockClose] = useState(false);

	const liveTrackName = 'Live Tracks';
	// const noTrackName = 'bl_untracked';

	useEffect(() => {
		// Reset tracked options
		if (sessionDisplayAll.onDemandSessions) {
			// MAKE SURE ALL OnDemand Tracks are SELECTED
			const defaultDisplayAll = getAllTrackNames(sessions).reduce((a, v) => ({ ...a, [v]: true }), {});
			setTrackedSessionDisplayAll({
				...sessionDisplayAll,
				...defaultDisplayAll
			});
		}
		else {
			setTrackedSessionDisplayAll(sessionDisplayAll);
		}

		const normalizedDisplayOptions = fillOutSessionDisplayOptions(sessionDisplayOptions, sessionDisplayAll, sessions, false);
		setTrackedSessionDisplayOptions(normalizedDisplayOptions);

		// Reset search
		setFilteredSessions(sessions);
		setSearching(false);

		// Reset group reference, this is not subject to the search and is static for session
		const { live, onDemand } = groupSessionsByTypeAndTrack(sessions, normalizedDisplayOptions, sessionDisplayAll);
		setAllBroadcastTracks(live);
		setAllOndemandTracks(onDemand);

		// Reset Hide/Show grouping
		let { show_broadcast_sessions, show_on_demand_sessions } = moduleContent;
		// This state can happen initially when only on-demand or live is present
		// Which is the initial state
		if (show_broadcast_sessions === undefined || show_on_demand_sessions === undefined) {
			// if any sessions exist for on-demand, enable on demand toggle
			const onDemand = sessions.find(({ session_type: type }) => type === SessionTypesEnum.onDemand);
			const liveSessions = sessions.find(({ session_type: type }) => scheduledSessionTypes.includes(type));

			show_on_demand_sessions = onDemand ? true : false;
			show_broadcast_sessions = liveSessions ? true : false;
		}
		setShowBroadcast(show_broadcast_sessions);
		setShowOnDemandSessions(show_on_demand_sessions);
	}, [modalIsOpen, moduleContent, sessionDisplayOptions, sessionDisplayAll, sessions]);

	const groupSessionsByTypeAndTrack = (
		sessions: (Session | SessionPreview)[],
		displayOptions: SessionDisplayOptions,
		displayAll: SessionDisplayAll,
	): { onDemand: { [key: string]: (Session | SessionPreview)[] }, live: { [key: string]: (Session | SessionPreview)[] } } => {
		const _ondemandTracks = getOnDemandSessionsSelectedForDisplay(displayOptions, displayAll, sessions, true);
		const _broadcastTracks: { [key: string]: (Session | SessionPreview)[] } = {};

		const pushTrack = (
			collection: { [key: string]: (Session | SessionPreview)[] },
			tracks: string[],
			session: Session | SessionPreview,
		) => tracks?.map(track => {
			if (!(track in collection)) {
				collection[track] = [session];
			}
			else {
				collection[track].push(session);
			}
		});

		sessions.map(session => {
			if (scheduledSessionTypes.includes(session.session_type)) {
				const tracks = [liveTrackName]; // Replace with track code from on demand to enable track grouping
				pushTrack(_broadcastTracks, tracks, session);
			}
		});

		return {
			onDemand: _ondemandTracks,
			live: _broadcastTracks,
		};
	};
	// Track the items that are filtred, this is subject to a user searching
	// Setting in useRef to allow using in filteredSessions without 
	// causing a refresh
	const filteredOptions = useRef({ trackedSessionDisplayAll, trackedSessionDisplayOptions });
	useEffect(() => {
		filteredOptions.current = { trackedSessionDisplayAll, trackedSessionDisplayOptions };
	}, [trackedSessionDisplayAll, trackedSessionDisplayOptions]);
	useEffect(() => {
		const { live, onDemand } = groupSessionsByTypeAndTrack(
			filteredSessions,
			filteredOptions.current.trackedSessionDisplayOptions,
			filteredOptions.current.trackedSessionDisplayAll);
		setBroadcastTracks(live);
		setOndemandTracks(onDemand);
	}, [filteredSessions]);

	const handleClearSearch = () => {
		setFilteredSessions(sessions);
		setSearching(false);
	};
	const handleSearch = (search: string) => {
		const trimmedSearch = search.toLowerCase().trim();
		const filtered = sessions.filter((session) => {
			const titles = session.title;
			const localizedTitles = Object.values(titles) as string[];
			const matches = localizedTitles.filter(
				(localTitle) =>
					localTitle.toLowerCase().includes(trimmedSearch)
			);
			return matches.length > 0;
		});

		setFilteredSessions(filtered);
		if (trimmedSearch === '') {
			setSearching(false);
			return;
		}
		setSearching(true);
	};


	/**
	 * Live sessions can be in any tracks.  If they are present
	 * and selected in one, they are present and selected in all.
	 * @param sessionId sessionId to check for being tracked
	 * @returns boolean, tracked or not
	 */
	const isLiveSessionSelected = (sessionId: number): boolean => {
		if (trackedSessionDisplayAll.liveSessions) return true;

		const allTrackedSessions = Object.values(trackedSessionDisplayOptions).flat();
		const exists = allTrackedSessions.includes(sessionId);
		return exists;
	};

	const isSessionSelected = (track: string, sessionId: number): boolean => {
		if (track === liveTrackName) { return isLiveSessionSelected(sessionId); }
		if (trackedSessionDisplayAll.onDemandSessions) return true;
		if (trackedSessionDisplayAll[track]) return true;

		const trackedSessions = trackedSessionDisplayOptions[track];

		if (!isArray(trackedSessions)) {
			console.warn(`Can't find tracked sessions for ${track}: ${JSON.stringify(trackedSessionDisplayOptions)}`);
			return false;
		}
		const selected = trackedSessions.includes(sessionId);
		return selected;
	};

	const getSessionAdded = (trackName: string, sessionId: number): number[] => {
		const sessions = trackedSessionDisplayOptions[trackName] || [];
		const workingTrack = [...sessions];

		if (!workingTrack.includes(sessionId)) {
			workingTrack.push(sessionId);
		}
		return workingTrack;
	};

	const getSessionRemoved = (trackName: string, sessionId: number): number[] => {
		if (!trackedSessionDisplayOptions[trackName]) {
			console.warn(`Unable to find track ${trackName}, to set session ${sessionId} to active. ${JSON.stringify(trackedSessionDisplayOptions)}`);
			return [];
		}
		const workingTrack = [...trackedSessionDisplayOptions[trackName]].filter(sid => sid !== sessionId);
		return workingTrack;
	};

	/**
	 * Checks that all items have been selected for live and enabled select all
	 * When all items have NOT been selected, live is DE-selected
	 * @param track string track that is being modified
	 * @param workingTrack array of selected items; modified track value before save happens
	 */
	const checkSelectAllForLiveSessions = (sessionId: number, enabled: boolean): void => {
		let allLiveSelected = false;

		// If not enabling, no reason to check
		if (enabled) {
			const allLiveIds = allBroadcastTracks[liveTrackName]
				.map(session => session.session)
				.flat();
			const countAllLive = allLiveIds.length;

			// filter to only the live session ids
			const selectedLiveIds = Object.values(trackedSessionDisplayOptions)
				.flat()
				.filter(session => allLiveIds.includes(session));
			// Add newly selected session
			selectedLiveIds.push(sessionId);
			// De-dupe ids
			const countLiveSelected = [...new Set(selectedLiveIds)].length;
			allLiveSelected = (countAllLive === countLiveSelected);
		}

		setTrackedSessionDisplayAll((prev) => ({ ...prev, liveSessions: allLiveSelected }));
	};
	/**
	 * Checks that all items have been selected for onDemand and enabled select all
	 * When all items have NOT been selected, onDemand is DE-selected
	 * @param track string track that is being modified
	 * @param workingTrack array of selected items; modified track value before save happens
	 */
	const checkSelectAllForOnDemand = (track: string, workingTrack: number[]): void => {
		const newWorkingSet = { ...trackedSessionDisplayOptions, [track]: workingTrack };
		const allOnDemandIds = Object.keys(allOndemandTracks)
			.map(key => allOndemandTracks[key]
				.map(session => session.session)).flat();
		const countAllOnDemand = allOnDemandIds.length;

		// filter to only the on-demand sessions
		const countAllOnDemandSelected = Object.values(newWorkingSet)
			.flat()
			.filter(session => allOnDemandIds.includes(session))
			.length;
		const selectAllForOnDemand = (countAllOnDemand === countAllOnDemandSelected);

		setTrackedSessionDisplayAll((prev) => ({ ...prev, onDemandSessions: selectAllForOnDemand }));
	};
	const checkSelectAllForTrack = (track: string, workingTrack: number[]): void => {
		// Working Track could have live items, ensure count is for ondemand only
		const countOnDemandSelected = allOndemandTracks[track]
			.filter(session => workingTrack.includes(session.session)).length;
		const selectAllForTrack = (countOnDemandSelected === allOndemandTracks[track].length);
		setTrackedSessionDisplayAll((prev) => ({ ...prev, [track]: selectAllForTrack }));
	};
	const checkExpandGroupNeeded = (track: string, enabled: boolean): void => {
		if (!enabled) return;
		if (track === liveTrackName && !showBroadcast) {
			setShowBroadcast(true);
			setExpandedBroadcast(true);
		}
		else if (track !== liveTrackName && !showOnDemandSessions) {
			setShowOnDemandSessions(true);
			setExpandedOndemand(true);
		}
	};
	/**
	 * Special handler for LIVE tracks.  Iterate over all the tracks in the 
	 * live session to select or de-select.  This allows for graceful display
	 * after the session transitions to on-demand.
	 * @param sessionId SessionId to get tracks for
	 * @param enabled Enabled or disabled
	 * @returns void
	 */
	const handleLiveSesionToggled = (sessionId: number, enabled: boolean): void => {
		const session = sessions.find(s => s.session === sessionId);
		if (!session) {
			console.warn(`Unable to find live session, not updating.  sessionId: ${sessionId}, sessions: ${JSON.stringify(sessions)}`);
			return;
		}
		// Recurse over tracks and toggle sessionf or each track.
		// Do _not_ check for needed expation, that has already been done on initial call
		// And could cause problems if live session is part of a track also in ondemand.
		if (!session.tracks || session.tracks?.length === 0) { handleSessionToggled(noTrackName, sessionId, enabled, false, false); }
		else { session.tracks.map(track => handleSessionToggled(track, sessionId, enabled, false, false)); }

		checkSelectAllForLiveSessions(sessionId, enabled);
	};
	const handleSessionToggled = (track: string, sessionId: number, enabled: boolean, checkExpandGroup = true, checkSelectAll = true): void => {
		if (checkExpandGroup) checkExpandGroupNeeded(track, enabled);

		// if session live, recurse over tracks
		if (track === liveTrackName) {
			handleLiveSesionToggled(sessionId, enabled);
			return;
		}

		let workingTrack: number[];
		if (enabled) workingTrack = getSessionAdded(track, sessionId);
		else workingTrack = getSessionRemoved(track, sessionId);


		const uniqueValues = new Set(workingTrack);
		setTrackedSessionDisplayOptions((prev) => {
			return (
				{
					...prev,
					[track]: [...uniqueValues]
				});
		});

		if (checkSelectAll) {
			checkSelectAllForTrack(track, workingTrack);
			checkSelectAllForOnDemand(track, workingTrack);
		}
	};

	const saveSessionDisplayOptions = async () => {
		if (!token) return;

		setSavingSessions(true);

		const updatedPageModule = JSON.parse(JSON.stringify(module)) as PageModule;
		if (!updatedPageModule.module_display_info
			|| !updatedPageModule.module_display_info.session_display_options) {
			updatedPageModule.module_display_info = {
				...updatedPageModule.module_display_info,
				session_display_options: {},
			};
		}
		// Individual display options
		updatedPageModule.module_display_info.session_display_options = trackedSessionDisplayOptions;
		// Display all settings
		updatedPageModule.module_display_info.session_display_all = trackedSessionDisplayAll;
		// Toggle for each section
		updatedPageModule.content = {
			...module.content,
			show_broadcast_sessions: showBroadcast,
			show_on_demand_sessions: showOnDemandSessions,
		};
		await UpdatePageModule(token, updatedPageModule);
		dispatchModuleChanges(updatedPageModule);

		setSavingSessions(false);

		// Use passed in close function so as not to manipualte state before saving is complete
		handleClearSearch();
		setModalClosed();
	};

	const dispatchModuleChanges = (updatedPageModule: PageModule) => {
		if (isCustomPage) {
			if (!customPageId) return; // Handled on entry, customPageId can not be null
			dispatch(updateCustomPageModule(customPageId, updatedPageModule));
		}
		if (isLandingPage || isHomePage) {
			// reducer function detects where the data goes
			// based on state.CreateEventReduer.currentPostRegisterPage === "home"
			// or null
			dispatch(updateHomepageModule(updatedPageModule));
		}
	};

	const handleSelectAllLive = (group: (Session | SessionPreview)[]) => {
		const workingDisplayOps = { ...trackedSessionDisplayOptions };

		group.forEach((session) => {
			const tracks = session.tracks || [];
			if (tracks.length === 0) tracks.push(noTrackName);
			tracks?.forEach((trackId) => {
				if (!workingDisplayOps[trackId]) workingDisplayOps[trackId] = [];
				workingDisplayOps[trackId].push(session.session);
				workingDisplayOps[trackId] = [...new Set(workingDisplayOps[trackId])];
			});
		});

		setTrackedSessionDisplayAll((prev) => ({ ...prev, liveSessions: true }));
		setTrackedSessionDisplayOptions(workingDisplayOps);
	};
	const handleSelectAll = (track: string, group: (Session | SessionPreview)[]) => {
		checkExpandGroupNeeded(track, true);

		if (track === liveTrackName) return handleSelectAllLive(group);

		const contentIds = group.map(session => session.session);
		// When selecting all, it is possible the passed in group will
		// not have all the values; some may be Live; which are not showing in the track grouping
		const existing = trackedSessionDisplayOptions[track] ? trackedSessionDisplayOptions[track] : [];
		const combinedTracks = [...contentIds, ...existing];
		const uniqueTracks = [...new Set(combinedTracks)];
		setTrackedSessionDisplayOptions((prev) => {
			return (
				{
					...prev,
					[track]: [...uniqueTracks],
				});
		});

		setTrackedSessionDisplayAll((prev) => ({ ...prev, [track]: true }));
		checkSelectAllForOnDemand(track, uniqueTracks);
	};
	const handleDeselectAllLive = (group: (Session | SessionPreview)[]) => {
		const workingDisplayOps = { ...trackedSessionDisplayOptions };

		group.forEach((session) => {
			const tracks = session.tracks || [];
			if (tracks.length === 0) tracks.push(noTrackName);
			tracks?.forEach((trackId) => {
				if (!workingDisplayOps[trackId]) workingDisplayOps[trackId] = [];
				workingDisplayOps[trackId] = workingDisplayOps[trackId].filter(sessionId => session.session != sessionId);
				workingDisplayOps[trackId] = [...new Set(workingDisplayOps[trackId])];
			});
		});

		setTrackedSessionDisplayAll((prev) => ({ ...prev, liveSessions: false }));
		setTrackedSessionDisplayOptions(workingDisplayOps);
	};
	const handleDeselectAll = (track: string, group: (Session | SessionPreview)[]) => {
		// Live is special, because the sessions can be in multiple tracks
		if (track === liveTrackName) return handleDeselectAllLive(group);

		// Possible track holds both live and on-demand.  Only remove
		// items that are part of the passed in set
		const contentIds = group.map(session => session.session);
		const filteredTracks = trackedSessionDisplayOptions[track]?.filter(sessionId => !(contentIds.includes(sessionId)));

		const uniqueTracks = [...new Set(filteredTracks)];
		setTrackedSessionDisplayOptions((prev) => {
			return (
				{
					...prev,
					[track]: [...uniqueTracks],
				});
		});
		// No longer have select all for track or onDemand
		setTrackedSessionDisplayAll((prev) => ({ ...prev, [track]: false, onDemandSessions: false }));
	};

	const generateCarouselControls = (
		track: string,
		group: (Session | SessionPreview)[],
		handleSelectAll: (track: string, group: (Session | SessionPreview)[]) => void,
		handleDeselectAll: (track: string, group: (Session | SessionPreview)[]) => void,
		allSelected: boolean,
		allowColapse?: boolean,
		isExpanded?: boolean,
		setIsExpanded?: () => void,
	) => {

		if (searching) return null;
		const text = allSelected ? "Deselect All" : "Select All";
		const onClick = allSelected ? () => { handleDeselectAll(track, group); } : () => { handleSelectAll(track, group); };

		return (
			<div className="expand-dropdown">
				<span className='select-all' onClick={onClick}>{text}</span>
				{allowColapse && <div className={classNames('new-dropdown-icon', { 'isopen': isExpanded, 'isnotopen': !isExpanded })} onClick={setIsExpanded}></div>}
			</div>
		);
	};

	const [trackExpanded, setTrackExpanded] = useState<({ [key: string]: boolean })>({});
	const toggleTrackExpanded = (track: string) => {
		setTrackExpanded(prev => ({
			...prev,
			[track]: !prev[track],
		}));
	};
	const isTrackExpanded = (track: string): boolean => {
		if (!isBoolean(trackExpanded[track])) trackExpanded[track] = true;
		return trackExpanded[track];
	};

	const generateCarousel = (track: string, group: (Session | SessionPreview)[]) => {
		const allSelected = trackedSessionDisplayAll[track];
		const isExpanded = isTrackExpanded(track);
		const setIsExpanded = () => toggleTrackExpanded(track);

		let carouselControls;
		let displayHeader = false;
		if (track !== liveTrackName) {
			carouselControls = generateCarouselControls(
				track,
				group,
				handleSelectAll,
				handleDeselectAll,
				allSelected,
				true,
				isExpanded,
				setIsExpanded,
			);
			displayHeader = true;
		}

		return (
			<AgendaCarousel
				isEditor
				track={track}
				group={group}
				displayHeaderText={displayHeader}
				carouselControls={carouselControls}
				setBlockClose={setBlockClose}
			>
				{group.map((session, i) => {
					const isSelected = isSessionSelected(track, session.session);
					return (
						<React.Fragment key={session.uuid}>
							<CarouselCard
								track={track}
								index={i}
								toggleCard={handleSessionToggled}
								selected={isSelected}
								session={session}
							/>
						</React.Fragment>
					);
				})}
			</AgendaCarousel>
		);
	};

	const [showBroadcast, setShowBroadcast] = useState(moduleContent.show_broadcast_sessions);
	const [showOnDemandSessions, setShowOnDemandSessions] = useState(moduleContent.show_on_demand_sessions);
	const toggleBroadcast = (_: string, isOpen: boolean) => {
		if (isOpen) {
			// Allow the boardcast group to expand
			setExpandedBroadcast(true);
		}
		else {
			// deselect all sessions and hide the grouping
			handleDeselectAll(liveTrackName, broadcastTracks[liveTrackName]);
			setExpandedBroadcast(false);
		}

		setShowBroadcast((prev) => !prev);
	};
	const toggleOndemand = (_: string, isOpen: boolean) => {
		if (isOpen) {
			// Allow the on demand group to expand
			setExpandedOndemand(true);
		}
		else {
			// deselect all sessions for all tracks in ondemand and hide the grouping
			Object.keys(ondemandTracks).forEach((trackName) =>
				handleDeselectAll(trackName, ondemandTracks[trackName]));
			setExpandedOndemand(false);
		}

		setShowOnDemandSessions((prev) => !prev);
	};

	const groupControlsForLive = () => {
		const track = liveTrackName;
		const group = broadcastTracks[liveTrackName] || [];
		const allSelected = trackedSessionDisplayAll.liveSessions;
		return generateCarouselControls(track, group, handleSelectAll, handleDeselectAll, allSelected, false);
	};
	
	const handleOpenReorderModal = (): void => {
		openReorderModal();
		setModalClosed();
	};

	return (
		<ModalComponent
			addMaxHeightClass={true}
			className="agenda-sessions-management-modal"
			header={
				<div className={classNames('agenda-sessions-management')}>
					<Search
						className="manage-content-search"
						onClear={handleClearSearch}
						onSearch={handleSearch}
					/>
				</div>
			}
			cancellable={false}
			closeable={false}
			open={modalIsOpen}
			noSidePadding={true}
			onRequestClose={blockClose ? () => null : setModalClosed}
			title={"Manage content"}
			size={'medium'}
			footer={(
				<>
					<button className="pull-left" onClick={handleOpenReorderModal}>Re-order content</button>
					<button onClick={blockClose ? () => null : setModalClosed} >Cancel</button>
					<button onClick={saveSessionDisplayOptions} className="primary" disabled={savingSessions}>{savingSessions ? <WaitingIndicator /> : "Save"}</button>
				</>
			)}
		>
			<div className={classNames('agenda-sessions-management')}>
				<CarouselGroupDropdown
					groupTitle='Live Sessions'
					isOpen={expandedBroadcast}
					onExpand={() => { setExpandedBroadcast(prev => !prev); }}
					onGroupToggle={toggleBroadcast}
					groupToggleValue={showBroadcast}
					contentGroup={broadcastTracks}
					contentGroupDisplayOrder={[liveTrackName]}
					generateCarousel={generateCarousel}
					groupControls={groupControlsForLive}
					resultsFiltered={searching}
				/>

				<CarouselGroupDropdown
					groupTitle='On Demand Sessions'
					isOpen={expandedOndemand}
					onExpand={() => { setExpandedOndemand(prev => !prev); }}
					onGroupToggle={toggleOndemand}
					groupToggleValue={showOnDemandSessions}
					contentGroup={ondemandTracks}
					contentGroupDisplayOrder={ondemandTrackOrder}
					generateCarousel={generateCarousel}
					resultsFiltered={searching}
				/>

			</div>
		</ModalComponent>
	);


});

ManageAgenda.displayName = 'ManageAgenda';

export default ManageAgenda;
